/**
 * PPOM Application Router Configuration
 *
 * This module provides minimal Vue Router configuration for the PPOM application.
 * Note: PPOM uses MultiViewManager for dynamic component loading instead of traditional routing.
 * The router is only needed to suppress Vue Router warnings and provide URL synchronization.
 */

import {
  createRouter,
  createWebHistory,
  type Router,
  type RouteRecordRaw,
  type RouteLocationNormalized,
  type NavigationGuardNext,
} from 'vue-router'
import { pageData } from '@shared/config/menuConfig'
import type { App } from 'vue'

// Re-export pageData for use in components
export { pageData }

/**
 * Scroll behavior position interface
 */
interface ScrollPosition {
  top: number
  left?: number
}

/**
 * Saved scroll position from router
 */
interface SavedPosition {
  left: number
  top: number
}

/**
 * Route configuration
 * - /auth/callback: OAuth callback 獨立頁面，處理 PKCE code / hash token exchange
 * - catch-all: MultiViewManager handles actual component loading
 */
const routes: RouteRecordRaw[] = [
  {
    path: '/auth/callback',
    name: 'auth-callback',
    component: () => import('@/views/auth/AuthCallback.vue'),
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'catch-all',
    component: { render: () => null },
  },
]

/**
 * Create the Vue Router instance
 */
const router: Router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(
    _to: RouteLocationNormalized,
    _from: RouteLocationNormalized,
    savedPosition: SavedPosition | null
  ): ScrollPosition {
    if (savedPosition) {
      return savedPosition
    } else {
      return { top: 0 }
    }
  },
})

/**
 * Router plugin for Vue application
 *
 * Installs the router and adds pageData to global properties
 */
export interface RouterPlugin {
  install: (app: App) => void
}

const routerPlugin: RouterPlugin = {
  install: (app: App): void => {
    // Add pageData to global properties for component access
    app.config.globalProperties.pageData = pageData

    // Install the router
    app.use(router)
  },
}

export default routerPlugin
